import { ArrowRight, ExternalLink, Mail, Phone, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const BOOK_URL = "https://reliservepulse.com/book-consultation";

const FAQS = [
  {
    q: "How do you get paid?",
    a: "We earn a commission from the mutual funds and insurance policies we help you invest in, paid by the AMC/insurer, not by you directly.",
  },
  {
    q: "Is my money safe with a distributor rather than going directly to an AMC?",
    a: "Yes. Your investments are held in your own name, directly with the mutual fund or insurer — we never hold your money ourselves. Our role is planning and execution support, not custody.",
  },
  {
    q: "What's the difference between a Direct Plan and a Regular Plan, and which do I get?",
    a: "We offer Regular Plans, which include the planning and ongoing review support outlined in our process — this is reflected in a small difference in expense ratio compared to Direct Plans, which offer no such support.",
  },
  {
    q: "What's the minimum amount I need to start?",
    a: "SIPs can start from as little as ₹500–1,000/month; there's no minimum to have an initial planning conversation.",
  },
  {
    q: "What if my goals or income change later?",
    a: "That's expected, and it's exactly why we review your plan periodically rather than setting it once and forgetting it. Reach out any time something changes — we'll revisit the plan together.",
  },
  {
    q: "Can I exit or redeem my investments whenever I want?",
    a: "Yes — your money stays fully yours and accessible, subject to the specific fund's exit load or lock-in terms (if any), which we'll always flag clearly before you invest.",
  },
  {
    q: "How are you different from my bank's relationship manager?",
    a: "A bank RM typically can only offer their own bank's products. As independent distributors, we can access funds and policies across providers and build a plan around what suits your goals — not what one institution is selling that quarter.",
  },
  {
    q: "Is my personal and financial information kept confidential?",
    a: "Yes. Your information is used solely to plan and service your account and is not shared with third parties beyond what's required to process your investments or policies.",
  },
];

export default function Contact() {
  return (
    <div>
      {/* Hero */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Contact</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            Let's build your<br /><span className="italic text-brass-deep">strategy</span>.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            Whether you're starting to plan for a goal, want to make sure it's properly protected, or
            just want to understand where inflation is quietly working against you — we're here to help.
          </p>
        </div>
      </section>

      {/* External booking CTA — prominent, at top */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="card-paper p-8 md:p-12 text-center" data-testid="contact-book-card">
            <div className="eyebrow mb-4">Book a Consultation</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-2xl mx-auto">
              Pick a time that works for you.
            </h2>
            <p className="mt-6 text-[15.5px] text-ink-soft leading-relaxed max-w-xl mx-auto">
              Our short scheduling form confirms a slot instantly — no back-and-forth email tag.
              It takes about a minute.
            </p>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brass mt-8"
              data-testid="contact-cta-book"
            >
              Book a Consultation <ArrowRight size={16} />
            </a>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-ink-soft/85">
              <ExternalLink size={11} /> Opens reliservepulse.com in a new tab
            </p>
            <div className="mt-8 pt-6 border-t hairline">
              <a
                href="https://chat.whatsapp.com/GtdGWz5Ydj2D5zTn22qVBx?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[14px] text-brass-deep hover:text-brass transition-colors group"
                data-testid="contact-whatsapp-community"
              >
                <WhatsAppIcon size={15} />
                <span className="border-b border-brass/30 group-hover:border-brass transition-colors">
                  Or join our WhatsApp community
                </span>
              </a>
              <p className="mt-2 text-[12px] text-ink-soft/85">
                Casual updates, planning tips, and answers to common questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="eyebrow mb-3">FAQ</div>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                Questions people ask<br />before reaching out.
              </h2>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full" data-testid="contact-faq">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b hairline">
                    <AccordionTrigger className="text-left font-display text-[17px] md:text-[18px] text-ink hover:text-brass-deep hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[14.5px] text-ink-soft leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Details — compact bottom block */}
      <section>
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="eyebrow mb-3">Details</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
            Reach us the way you prefer.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-8">
            <DetailRow icon={<Phone size={18} />} label="Phone" value="+91 70289 77777" href="tel:+917028977777" />
            <DetailRow icon={<Mail size={18} />} label="Email" value={
              <a href="mailto:contact@reliservewealth.com" className="link-brass" data-testid="contact-email-link">
                contact@reliservewealth.com
              </a>
            } />
            <DetailRow icon={<MapPin size={18} />} label="Office" value={
              <>
                Reliserve Wealth<br />
                #304, 3rd Floor, Laxmi Towers,<br />
                Swami Vivekananda Road,<br />
                Opp Junta House,<br />
                Panaji 403 001, Goa, INDIA
              </>
            } />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailRow({ icon, label, value, href }) {
  const inner = <div className="num text-[14px] text-ink mt-1 leading-relaxed">{value}</div>;
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full border hairline flex items-center justify-center text-brass shrink-0">{icon}</div>
      <div>
        <div className="eyebrow">{label}</div>
        {href ? (
          <a href={href} className="block hover:text-brass-deep transition-colors">
            {inner}
          </a>
        ) : (
          inner
        )}
      </div>
    </div>
  );
}
